/**
 * demoBlueprints.js — synthetic entity generators for the demo ENRICHER.
 *
 * When demo mode is on and a screen's list comes back empty (or nearly empty)
 * for a small/fresh tenant, {@link module:app/demo/demoEnricher} tops it up with
 * fabricated rows so the app looks like a busy, live system. This module owns the
 * per-resource "blueprints": each is a pure function
 * `(rng, index, ctx) => entity` that returns ONE believable record shaped exactly
 * the way the corresponding screen reads it (field names taken from a menu-wide
 * screen→endpoint audit).
 *
 * Design rules:
 *   • Deterministic. Every value comes from the seeded `rng`
 *     ({@link module:app/demo/demoFaker}.createRng) so the same screen renders the
 *     same fabricated rows across refetches — no flicker, no reshuffle.
 *   • Coherent, not random noise. Statuses use weighted distributions correlated
 *     with age (older orders are closed, recent ones live), money adds up
 *     (orderSum = products + fees + tip), dates land inside the requested window.
 *   • Crash-safe field types. Fields the client does arithmetic or `.length` on
 *     (e.g. `order.orderFrom.length === 24` in StatisticsHelper) are ALWAYS the
 *     right primitive type, never null/undefined.
 *   • PII goes through demoFaker so a fabricated customer is consistent with the
 *     redaction layer's fakes.
 *
 * @module app/demo/demoBlueprints
 */

import {
    createRng,
    fakeCompany,
    fakePerson,
    fakeTaxNumber,
    fakeIban,
    fakeCity,
    fakeStreet,
    fakeVenueName,
} from './demoFaker';

/* ───────────────────────── shared pools ───────────────────────── */

/** Hungarian menu items for fabricated order lines (synthetic, not the real menu). */
const DISHES = [
    {hu: 'Gulyásleves', en: 'Goulash soup', price: 1690},
    {hu: 'Húsleves', en: 'Broth', price: 1290},
    {hu: 'Rántott sajt', en: 'Fried cheese', price: 2490},
    {hu: 'Bécsi szelet', en: 'Wiener schnitzel', price: 3290},
    {hu: 'Csirkepaprikás', en: 'Chicken paprikash', price: 2890},
    {hu: 'Marhapörkölt', en: 'Beef stew', price: 3490},
    {hu: 'Hortobágyi palacsinta', en: 'Hortobágy pancake', price: 2190},
    {hu: 'Halászlé', en: 'Fisherman soup', price: 2790},
    {hu: 'Margaréta pizza', en: 'Margherita pizza', price: 2390},
    {hu: 'Hamburger sültkrumplival', en: 'Burger with fries', price: 2990},
    {hu: 'Cézár saláta', en: 'Caesar salad', price: 2590},
    {hu: 'Somlói galuska', en: 'Somló sponge cake', price: 1490},
    {hu: 'Sajttorta', en: 'Cheesecake', price: 1390},
    {hu: 'Limonádé', en: 'Lemonade', price: 890},
    {hu: 'Kávé', en: 'Coffee', price: 690},
    {hu: 'Kézműves sör', en: 'Craft beer', price: 1190},
];

/** Delivery / channel platforms for a takeaway or online order (always a string). */
const ONLINE_SOURCES = ['wolt', 'foodora', 'netpincer', 'webshop', 'bolt'];
const TAKEAWAY_SOURCES = ['phone', 'webshop', 'wolt', 'foodora'];

/** Vehicle makes for service work orders. */
const CAR_MAKES = [
    ['Volkswagen', ['Golf', 'Passat', 'Polo', 'Tiguan']],
    ['Opel', ['Astra', 'Corsa', 'Insignia']],
    ['Ford', ['Focus', 'Fiesta', 'Mondeo']],
    ['Toyota', ['Corolla', 'Yaris', 'RAV4']],
    ['Škoda', ['Octavia', 'Fabia', 'Superb']],
    ['BMW', ['320d', '520d', 'X3']],
    ['Audi', ['A4', 'A6', 'Q5']],
    ['Suzuki', ['Swift', 'Vitara', 'SX4']],
];

/* ───────────────────────── shared builders ───────────────────────── */

/** Random Hungarian licence plate, new format (AABB-123). */
const plate = (r) => {
    const L = 'ABCDEFGHIJKLMNPRSTUVWXYZ';

    return `${r.pick(L.split(''))}${r.pick(L.split(''))}${r.pick(L.split(''))}${r.pick(L.split(''))}-${r.int(100, 999)}`;
};

/** A fabricated address block. */
const address = (r, i) => ({
    zip: String(r.int(1010, 8900)),
    city: fakeCity(`demo-addr-${i}`),
    street: fakeStreet(`demo-addr-${i}`),
    houseNumber: String(r.int(1, 120)),
});

/** Build 1–5 fabricated order line items with coherent prices. */
const orderItems = (r) => {
    const n = r.int(1, 5);
    const out = [];
    for (let k = 0; k < n; k++) {
        const dish = r.pick(DISHES);
        const quantity = r.int(1, 3);
        out.push({
            item: {
                _id: r.hexId(),
                name: {hu: dish.hu, en: dish.en},
                sizes: [{name: {hu: 'Alap', en: 'Default'}, price: dish.price}],
                vat: {dineIn: 27, takeAway: 27, online: 27},
            },
            name: dish.hu,
            quantity,
            amount: quantity,
            price: dish.price,
            remotePrice: dish.price,
        });
    }

    return out;
};

/* ───────────────────────── blueprints ───────────────────────── */

/**
 * Order — THE linchpin record. POS statistics, the management dashboard and the
 * product/platform traffic screens all derive their charts CLIENT-SIDE from the
 * order array (StatisticsHelper / buildRevenueSeries), so a rich, coherent order
 * blueprint lights up every one of them at once.
 * @param {object} r seeded rng.
 * @param {number} i row index.
 * @param {object} ctx enrichment context ({startMs,endMs,nowMs,placeId,...}).
 * @returns {object} a synthetic order.
 */
function bpOrder(r, i, ctx) {
    const created = r.dateWithin(ctx.startMs, ctx.endMs);
    const ageDays = (ctx.nowMs - created.getTime()) / 86400000;
    const orderType = r.weighted([['dineIn', 5], ['takeAway', 3], ['online', 2]]);
    // dine-in POS orders carry the place ObjectId in orderFrom (StatisticsHelper
    // keys off `orderFrom.length === 24`); channel orders carry a platform string.
    const placeId = (ctx.placeId && String(ctx.placeId).length === 24) ? String(ctx.placeId) : r.hexId();
    const orderFrom = orderType === 'dineIn'
        ? placeId
        : (orderType === 'online' ? r.pick(ONLINE_SOURCES) : r.pick(TAKEAWAY_SOURCES));
    // Status correlated with age: older orders are settled, fresh ones are live.
    const status = ageDays > 1
        ? r.weighted([['closed', 9], ['cancelled', 1]])
        : r.weighted([['closed', 4], ['preparing', 2], ['ready', 2], ['pending', 1]]);
    const storno = r.chance(0.03);
    const isPayed = status === 'closed' && !storno;

    const items = orderItems(r);
    const productsSum = items.reduce((s, it) => s + it.price * it.quantity, 0);
    const serviceFeePrice = orderType === 'dineIn' ? Math.round(productsSum * 0.1) : 0;
    const deliveryFee = orderType === 'online' ? r.pick([0, 490, 590, 690]) : 0;
    const tipAmount = r.chance(0.2) ? r.pick([200, 500, 1000]) : 0;
    const orderSum = productsSum + serviceFeePrice + deliveryFee + tipAmount;
    const paymentType = r.weighted([['creditCard', 5], ['cash', 3], ['szepCreditCard', 1], ['vipBalance', 1]]);
    const person = fakePerson(`order-${i}`);
    const _id = r.hexId();

    return {
        _id,
        id: _id,
        orderCode: 100000 + ((r.int(0, 899999)) % 900000),
        generatedId: `#${100000 + (i % 900000)}`,
        created: created.toISOString(),
        createdAt: created.toISOString(),
        orderDate: created.toISOString(),
        closedAt: isPayed ? new Date(created.getTime() + r.int(20, 90) * 60000).toISOString() : null,
        updatedAt: created.toISOString(),
        status,
        storno,
        orderType,
        orderFrom,
        source: orderFrom,
        orderSum,
        productsSum,
        productsPrice: productsSum,
        originalSum: orderSum,
        deliveryFee,
        serviceFeePrice,
        serviceFee: serviceFeePrice,
        tipAmount,
        paymentType,
        paymentTypes: [{paymentType, amount: orderSum}],
        isPayed,
        needInvoice: r.chance(0.15),
        needEReceipt: false,
        printed: isPayed,
        synced: true,
        history: [],
        events: [],
        items,
        userInfo: {
            name: {first: person.first, last: person.last},
            firstName: person.first,
            lastName: person.last,
            phone: person.phone,
            mobilePhone: person.phone,
            email: person.email,
        },
        tableName: orderType === 'dineIn' ? String(r.int(1, 24)) : '',
        tableId: orderType === 'dineIn' ? r.hexId() : null,
        restaurantPlace: ctx.placeId || placeId,
        comment: '',
        discounts: [],
    };
}

/** Service work order / worksheet. */
function bpWorkOrder(r, i, ctx) {
    const [make, models] = r.pick(CAR_MAKES);
    const model = r.pick(models);
    const created = r.dateWithin(ctx.startMs, ctx.endMs);
    const scheduled = new Date(ctx.nowMs + r.int(-3, 10) * 86400000);
    // Skew toward active statuses: the worksheet list hides completed/invoiced in
    // its default "active" view, so a completed-heavy set would look empty there.
    const status = r.weighted([['in_progress', 4], ['pending', 3], ['completed', 3], ['invoiced', 2]]);
    const person = fakePerson(`wo-${i}`);
    const company = r.chance(0.4);
    const grandTotal = r.money(15000, 240000, 500);
    const _id = r.hexId();

    return {
        _id,
        id: _id,
        workOrderNumber: `MJ-${new Date(ctx.nowMs).getFullYear()}-${String(1000 + i).padStart(4, '0')}`,
        caseNumber: `MJ-${String(1000 + i).padStart(4, '0')}`,
        worksheetId: `MJ-${String(1000 + i).padStart(4, '0')}`,
        status,
        createdAt: created.toISOString(),
        updatedAt: created.toISOString(),
        scheduledAt: scheduled.toISOString(),
        scheduledTime: `${String(r.int(8, 16)).padStart(2, '0')}:00`,
        dueDate: scheduled.toISOString(),
        estimatedHours: r.int(1, 6),
        jobSummary: r.pick(['Olajcsere + szűrők', 'Fékbetét csere', 'Műszaki vizsga előkészítés',
            'Vezérműszíj csere', 'Klíma töltés', 'Futómű beállítás', 'Gumicsere + centírozás']),
        customer: company ? fakeCompany(`wo-cust-${i}`) : person.full,
        email: person.email,
        phone: person.phone,
        car: `${make} ${model}`,
        licensePlate: plate(r),
        plate: plate(r),
        vin: `WVW${r.hexId().toUpperCase().slice(0, 14)}`,
        year: r.int(2008, 2023),
        mileage: r.int(30000, 240000),
        fuel: r.pick(['Benzin', 'Dízel', 'Hibrid', 'Elektromos']),
        color: r.pick(['Fekete', 'Fehér', 'Szürke', 'Kék', 'Piros', 'Ezüst']),
        financials: {grandTotal},
        salePrice: grandTotal,
        parts: [],
        tasks: [],
    };
}

/** Booking / table reservation. */
function bpBooking(r, i, ctx) {
    const from = new Date(ctx.nowMs + r.int(-2, 12) * 86400000);
    from.setHours(r.int(11, 21), r.pick([0, 15, 30, 45]), 0, 0);
    const to = new Date(from.getTime() + r.int(1, 3) * 3600000);
    const person = fakePerson(`booking-${i}`);
    const _id = r.hexId();
    const status = r.weighted([['accepted', 5], ['pending', 3], ['denied', 1]]);

    return {
        _id,
        id: _id,
        status,
        // Denied bookings surface a reason on the detail screen.
        denyReason: status === 'denied'
            ? r.pick(['Nincs szabad asztal a kért időpontban', 'Zárva tartunk aznap', 'Telt ház'])
            : '',
        dateFrom: from.toISOString(),
        dateTo: to.toISOString(),
        guests: r.int(2, 12),
        table: String(r.int(1, 24)),
        phone: person.phone,
        comment: r.chance(0.3) ? r.pick(['Ablak melletti asztalt kérünk', 'Születésnap', 'Gyerekszék kell', 'Céges vacsora']) : '',
        guestInfo: {name: person.full, email: person.email, phone: person.phone},
        restaurant: {name: fakeVenueName(`venue-${ctx.restaurantId || 'demo'}`)},
    };
}

/** Service-module customer (with vehicles). */
function bpCustomer(r, i, ctx) {
    const company = r.chance(0.35);
    const person = fakePerson(`cust-${i}`);
    const addr = address(r, i);
    const [make, models] = r.pick(CAR_MAKES);
    const _id = r.hexId();
    const vehicles = Array.from({length: r.int(0, 2)}, (_, k) => ({
        _id: r.hexId(),
        brand: make,
        model: r.pick(models),
        plate: plate(r),
        year: r.int(2008, 2023),
        mileage: r.int(20000, 220000),
        vin: `W0L${r.hexId().toUpperCase().slice(0, 14)}`,
    }));

    return {
        _id,
        id: _id,
        name: company ? fakeCompany(`cust-co-${i}`) : person.full,
        companyName: company ? fakeCompany(`cust-co-${i}`) : '',
        isCompany: company,
        customerType: company ? 'company' : 'person',
        classification: r.pick(['VIP', 'Törzsvendég', 'Új', 'Normál']),
        email: person.email,
        phone: person.phone,
        mobile: person.phone,
        taxNumber: company ? fakeTaxNumber(`cust-${i}`) : '',
        address: `${addr.zip} ${addr.city}, ${addr.street} ${addr.houseNumber}.`,
        notes: '',
        worksheetCount: r.int(0, 18),
        lastVisit: new Date(ctx.nowMs - r.int(0, 120) * 86400000).toISOString(),
        vehicles,
    };
}

/** NAV invoice (inbound/outbound). Emits both flat + a couple of nested aliases. */
function bpInvoice(r, i, ctx) {
    const direction = ctx.direction || (r.chance(0.5) ? 'INBOUND' : 'OUTBOUND');
    const issue = r.dateWithin(ctx.startMs, ctx.endMs);
    const due = new Date(issue.getTime() + r.int(8, 30) * 86400000);
    const party = fakeCompany(`inv-${direction}-${i}`);
    const gross = r.money(12000, 890000, 100);
    const status = r.weighted([['paid', 5], ['issued', 3], ['overdue', 1], ['draft', 1]]);
    const number = `${new Date(issue).getFullYear()}/${String(1 + i).padStart(5, '0')}`;
    const _id = r.hexId();

    return {
        _id,
        id: _id,
        invoiceNumber: number,
        invoiceNo: number,
        direction,
        supplierName: direction === 'INBOUND' ? party : fakeVenueName(`venue-${ctx.restaurantId || 'demo'}`),
        customerName: direction === 'INBOUND' ? fakeVenueName(`venue-${ctx.restaurantId || 'demo'}`) : party,
        partnerName: party,
        buyerName: direction === 'INBOUND' ? fakeVenueName(`venue-${ctx.restaurantId || 'demo'}`) : party,
        grossAmount: gross,
        totalGross: gross,
        invoiceGrossAmount: gross,
        totals: {grossAmount: gross},
        invoiceIssueDate: issue.toISOString(),
        issueDate: issue.toISOString(),
        date: issue.toISOString(),
        createdAt: issue.toISOString(),
        dueDate: due.toISOString(),
        paymentDueDate: due.toISOString(),
        status,
        paymentStatus: status,
        invoiceStatus: status,
        deleted: false,
        invoiceData: {invoiceNumber: number},
    };
}

/** CRM lead. */
function bpLead(r, i, ctx) {
    const person = fakePerson(`lead-${i}`);
    const _id = r.hexId();

    return {
        _id,
        id: _id,
        name: person.full,
        company: fakeCompany(`lead-co-${i}`),
        email: person.email,
        phone: person.phone,
        value: r.money(50000, 4500000, 1000),
        status: r.weighted([['new', 3], ['contacted', 3], ['in_progress', 3], ['won', 2], ['lost', 1]]),
        source: r.pick(['Weboldal', 'Ajánlás', 'Telefon', 'Facebook', 'Google Ads', 'Kiállítás']),
        assignedTo: fakePerson(`lead-owner-${i % 4}`).full,
        notes: '',
        createdAt: r.dateWithin(ctx.startMs, ctx.endMs).toISOString(),
    };
}

/** CRM / sales quote. */
function bpQuote(r, i, ctx) {
    const total = r.money(80000, 3200000, 500);
    const _id = r.hexId();

    return {
        _id,
        id: _id,
        quoteNumber: `AJ-${new Date(ctx.nowMs).getFullYear()}-${String(1 + i).padStart(4, '0')}`,
        title: r.pick(['Rendezvény ajánlat', 'Catering csomag', 'Céges vacsora', 'Esküvői menü', 'Konferencia ellátás']),
        customerName: fakeCompany(`quote-${i}`),
        clientName: fakeCompany(`quote-${i}`),
        amount: total,
        totalAmount: total,
        grandTotal: total,
        status: r.weighted([['sent', 4], ['draft', 2], ['accepted', 3], ['rejected', 1]]),
        validUntil: new Date(ctx.nowMs + r.int(7, 30) * 86400000).toISOString(),
        notes: '',
    };
}

/** Service technician. */
function bpTechnician(r, i) {
    const person = fakePerson(`tech-${i}`);
    const _id = r.hexId();

    return {
        _id,
        id: _id,
        name: person.full,
        firstName: person.first,
        lastName: person.last,
        email: person.email,
        phone: person.phone,
        specialization: r.pick(['Motor', 'Futómű', 'Elektronika', 'Karosszéria', 'Gumiszerviz', 'Klíma']),
        skill: r.pick(['Junior', 'Senior', 'Mester']),
        status: r.weighted([['active', 5], ['busy', 2], ['off', 1]]),
    };
}

/** Service appointment. */
function bpAppointment(r, i, ctx) {
    const start = new Date(ctx.nowMs + r.int(-1, 10) * 86400000);
    start.setHours(r.int(8, 16), r.pick([0, 30]), 0, 0);
    const durationMinutes = r.pick([30, 60, 90, 120]);
    const tech = fakePerson(`tech-${i % 6}`).full;
    const _id = r.hexId();

    return {
        _id,
        id: _id,
        title: r.pick(['Olajcsere', 'Műszaki vizsga', 'Fékjavítás', 'Gumicsere', 'Diagnosztika']),
        customerName: fakePerson(`appt-${i}`).full,
        startTime: start.toISOString(),
        endTime: new Date(start.getTime() + durationMinutes * 60000).toISOString(),
        durationMinutes,
        location: r.pick(['1. szerelőakna', '2. szerelőakna', 'Gumisműhely', 'Diagnosztika']),
        status: r.weighted([['booked', 4], ['completed', 3], ['cancelled', 1]]),
        technician: {name: tech},
        technicianName: tech,
    };
}

/** Warehouse stock line. */
function bpStockItem(r, i, ctx) {
    const qty = r.weighted([[0, 1], [r.int(1, 4), 2], [r.int(5, 40), 4], [r.int(41, 250), 3]]);
    const _id = r.hexId();
    const name = r.pick(['Liszt BL-55', 'Kristálycukor', 'Napraforgó olaj', 'Paradicsom konzerv',
        'Mozzarella', 'Csirkemell filé', 'Sertéskaraj', 'Burgonya', 'Vöröshagyma', 'Tejföl 20%',
        'Trappista sajt', 'Rizs', 'Tészta penne', 'Ásványvíz 0,5l', 'Coca-Cola 0,33l']);

    return {
        _id,
        id: _id,
        name,
        item: {name, stock: qty, stockQty: qty},
        stock: qty,
        stockQty: qty,
        unit: r.pick(['kg', 'l', 'db', 'csomag']),
        warehouse: {name: r.pick(['Központi raktár', 'Hűtő', 'Fagyasztó', 'Száraz raktár'])},
    };
}

/** Warehouse transaction. */
function bpTransaction(r, i, ctx) {
    const _id = r.hexId();

    return {
        _id,
        id: _id,
        transactionType: r.pick(['inbound', 'outbound', 'transfer', 'writeoff']),
        type: r.pick(['inbound', 'outbound', 'transfer', 'writeoff']),
        createdAt: r.dateWithin(ctx.startMs, ctx.endMs).toISOString(),
        warehouse: {name: r.pick(['Központi raktár', 'Hűtő', 'Száraz raktár'])},
        partner: {name: fakeCompany(`txn-${i}`)},
    };
}

/** Warehouse (storage location). */
function bpWarehouse(r, i) {
    const _id = r.hexId();
    const name = r.pick(['Központi raktár', 'Hűtőraktár', 'Fagyasztó', 'Száraz raktár', 'Italraktár', 'Göngyöleg']);

    return {
        _id,
        id: _id,
        name,
        warehouseName: name,
        type: r.pick(['main', 'cold', 'dry']),
        location: `${r.int(1010, 8900)} ${fakeCity(`wh-${i}`)}`,
        isDefault: i === 0,
        isDefaultWarehouse: i === 0,
        default: i === 0,
    };
}

/** Bank account. */
function bpBank(r, i) {
    const _id = r.hexId();
    const name = r.pick(['OTP Bank', 'K&H Bank', 'Erste Bank', 'Raiffeisen', 'MBH Bank', 'UniCredit']);

    return {
        _id,
        id: _id,
        name,
        bankName: name,
        accountNumber: `${r.int(10000000, 99999999)}-${r.int(10000000, 99999999)}-${r.int(10000000, 99999999)}`,
        iban: fakeIban(`bank-${i}`),
        currency: 'HUF',
        balance: r.money(120000, 24000000, 1000),
    };
}

/** Cash register / point-of-sale drawer. */
function bpRegister(r, i) {
    const _id = r.hexId();
    const isOpen = r.chance(0.4);

    return {
        _id,
        id: _id,
        name: `Kassza ${i + 1}`,
        registerName: `Kassza ${i + 1}`,
        isOpen,
        status: isOpen ? 'open' : 'closed',
        balance: r.money(0, 480000, 500),
    };
}

/** Cost type (finance taxonomy). */
function bpCostType(r, i) {
    const _id = r.hexId();
    const name = r.pick(['Alapanyag', 'Bér', 'Rezsi', 'Marketing', 'Bérleti díj', 'Karbantartás',
        'Adók', 'Szállítás', 'Csomagolás', 'Eszközbeszerzés']);

    return {
        _id,
        id: _id,
        name,
        description: '',
        rootType: r.pick(['expense', 'income']),
        children: [],
    };
}

/** Integration card. */
function bpIntegration(r, i) {
    const provider = r.pick(['Wolt', 'Foodora', 'NTAK', 'Szamlazz.hu', 'NAV Online Számla',
        'Stripe', 'Barion', 'Google', 'Facebook', 'Shopify']);

    return {
        _id: r.hexId(),
        name: provider,
        provider,
        type: r.pick(['delivery', 'payment', 'accounting', 'marketing']),
        enabled: r.chance(0.6),
    };
}

/** Admin / staff user. */
function bpUser(r, i, ctx) {
    const person = fakePerson(`user-${i}`);
    const _id = r.hexId();
    const online = r.chance(0.35);

    return {
        _id,
        id: _id,
        name: {first: person.first, last: person.last},
        firstName: person.first,
        lastName: person.last,
        email: person.email,
        phone: person.phone,
        mobile: person.phone,
        username: `${person.first}.${person.last}`.toLowerCase().replace(/[^a-z.]/g, ''),
        role: r.pick(['manager', 'waiter', 'cook', 'cashier', 'admin']),
        roles: [r.pick(['restaurant:manager', 'restaurant:waiter', 'restaurant:cook', 'restaurant:cashier'])],
        status: r.pick(['active', 'active', 'active', 'inactive']),
        active: true,
        blocked: false,
        isLocked: false,
        online,
        isOnline: online,
        presence: online ? 'online' : 'offline',
        lastLoginAt: new Date(ctx.nowMs - r.int(0, 20) * 86400000).toISOString(),
    };
}

/** Automation rule. */
function bpAutomation(r, i) {
    const _id = r.hexId();
    const type = r.pick(['sendEmail', 'sendPush', 'sendSms', 'addTag', 'assignUser']);

    return {
        _id,
        id: _id,
        name: r.pick(['Új rendelés értesítő', 'Elhagyott kosár', 'VIP üdvözlő', 'Alacsony készlet riasztás',
            'Napi zárás emlékeztető', 'Foglalás visszaigazolás', 'Számla utókövetés']),
        moduleName: r.pick(['Order', 'Lead', 'Food', 'Booking', 'Invoice', 'Task']),
        triggerType: r.pick(['created', 'updated', 'statusChanged', 'scheduled']),
        enabled: r.chance(0.7),
        scope: {visibility: r.pick(['restaurant', 'private'])},
        actions: [{type, config: {}}],
    };
}

/** Guest (order-derived CRM guest). */
function bpGuest(r, i, ctx) {
    const person = fakePerson(`guest-${i}`);
    const addr = address(r, i);

    return {
        _id: r.hexId(),
        user: r.hexId(),
        userInfo: {
            name: {first: person.first, last: person.last},
            phone: person.phone,
            email: person.email,
        },
        addressInfo: {address: `${addr.street} ${addr.houseNumber}.`, city: addr.city, zip: addr.zip},
        totalSpent: r.money(5000, 680000, 100),
        ordersCount: r.int(1, 40),
    };
}

/** VIP loyalty card. */
function bpVipCard(r, i, ctx) {
    const person = fakePerson(`vip-${i}`);
    const _id = r.hexId();
    const historyLen = r.int(0, 24);
    const created = new Date(ctx.nowMs - r.int(30, 400) * 86400000);

    return {
        _id,
        id: _id,
        cardId: `VIP-${String(1000 + i).padStart(5, '0')}`,
        balance: r.weighted([[r.money(0, 25000, 500), 6], [-r.money(500, 8000, 500), 1]]),
        discountAmount: r.pick([0, 5, 10, 15, 20]),
        enableServiceFee: r.chance(0.5),
        created: created.toISOString(),
        createdAt: created.toISOString(),
        lastUsed: new Date(ctx.nowMs - r.int(0, 30) * 86400000).toISOString(),
        // Top-up / spend ledger — each entry timestamped so the detail history renders.
        history: Array.from({length: historyLen}, (_, k) => ({
            amount: r.money(500, 20000, 500),
            time: new Date(ctx.nowMs - r.int(0, 90) * 86400000 - k * 3600000).toISOString(),
        })),
        user: {
            name: {first: person.first, last: person.last},
            email: person.email,
            phoneNumbers: [{phone: person.phone}],
        },
    };
}

/** Calendar event (personal / service scheduling). */
function bpCalendarEvent(r, i, ctx) {
    const start = new Date(ctx.nowMs + r.int(-3, 14) * 86400000);
    start.setHours(r.int(8, 17), r.pick([0, 30]), 0, 0);
    const person = fakePerson(`cal-${i}`);
    const _id = r.hexId();

    return {
        _id,
        id: _id,
        title: r.pick(['Ügyfél egyeztetés', 'Szerviz időpont', 'Szállítói tárgyalás', 'Csapat meeting',
            'Bemutató', 'Karbantartás', 'Leltár']),
        description: '',
        start: start.toISOString(),
        end: new Date(start.getTime() + r.pick([30, 60, 90]) * 60000).toISOString(),
        client: person.full,
        clientEmail: person.email,
        clientPhone: person.phone,
        expert: {_id: r.hexId()},
        syncedWithGoogle: r.chance(0.3),
        syncedWithApple: false,
        notifications: {enabled: true, channels: {email: true, sms: false}, notifyExpert: true, reminderTime: 60},
    };
}

/** HACCP monitored equipment (fridge / freezer / warmer). */
function bpHaccpEquipment(r, i) {
    const _id = r.hexId();
    const type = r.pick(['fridge', 'freezer', 'coldRoom', 'warmer']);
    const cold = type === 'freezer' || type === 'coldRoom';
    const name = r.pick(['Konyhai hűtő', 'Italhűtő', 'Fagyasztóláda', 'Hűtőkamra', 'Pult hűtő',
        'Húselőkészítő hűtő', 'Cukrász hűtő', 'Melegen tartó']);

    return {
        _id,
        id: _id,
        name,
        type,
        minTemperature: cold ? r.int(-24, -18) : r.int(0, 2),
        maxTemperature: cold ? r.int(-17, -12) : r.int(4, 8),
        unit: '°C',
    };
}

/** HACCP measurement / checklist record. */
function bpHaccpRecord(r, i, ctx) {
    const _id = r.hexId();
    const person = fakePerson(`haccp-${i % 6}`);
    const equipmentName = r.pick(['Konyhai hűtő', 'Italhűtő', 'Fagyasztóláda', 'Hűtőkamra', 'Pult hűtő']);
    const measuredValue = r.int(-22, 7);
    const checkedAt = new Date(ctx.nowMs - r.int(0, 14) * 86400000 - r.int(0, 20) * 3600000);

    return {
        _id,
        id: _id,
        equipment: {name: equipmentName},
        equipmentName,
        title: r.pick(['Reggeli hőmérséklet ellenőrzés', 'Esti hőmérséklet ellenőrzés',
            'Áruátvétel ellenőrzés', 'Takarítási napló', 'Olaj minőség ellenőrzés']),
        module: r.pick(['temperature', 'cleaning', 'delivery']),
        status: r.weighted([['ok', 8], ['warning', 1], ['fail', 1]]),
        measuredValue,
        value: measuredValue,
        unit: '°C',
        checkedAt: checkedAt.toISOString(),
        createdAt: checkedAt.toISOString(),
        signedByName: person.full,
        signedBy: person.full,
    };
}

/** VoIP call-log entry (inbound/outbound, missed/answered). */
function bpVoipCall(r, i, ctx) {
    const _id = r.hexId();
    const person = fakePerson(`voip-${i}`);
    const incoming = r.chance(0.55);
    const missed = incoming && r.chance(0.2);
    const durationSeconds = missed ? 0 : r.int(20, 640);
    const started = new Date(ctx.nowMs - r.int(0, 14) * 86400000 - r.int(0, 23) * 3600000);
    const number = person.phone;

    return {
        _id,
        id: _id,
        number,
        from: incoming ? number : '',
        to: incoming ? '' : number,
        remoteNumber: number,
        contactName: person.full,
        name: person.full,
        direction: incoming ? 'inbound' : 'outbound',
        incoming,
        missed,
        status: missed ? 'missed' : r.weighted([['answered', 8], ['completed', 4]]),
        durationSeconds,
        duration: durationSeconds,
        createdAt: started.toISOString(),
        startedAt: started.toISOString(),
    };
}

/** VoIP directory / saved contact. */
function bpVoipContact(r, i) {
    const _id = r.hexId();
    const person = fakePerson(`voipc-${i}`);

    return {
        _id,
        id: _id,
        name: person.full,
        displayName: person.full,
        company: r.chance(0.4) ? fakeCompany(`voipc-co-${i}`) : '',
        email: person.email,
        note: '',
        favorite: r.chance(0.2),
        shared: r.chance(0.3),
        direction: r.pick(['inbound', 'outbound']),
        phone: person.phone,
        number: person.phone,
        phones: [{phone: person.phone, label: r.pick(['mobil', 'munkahelyi', 'otthoni'])}],
    };
}

/** Calendar expert / bookable colleague (for shared-calendar overlays). */
function bpCalendarExpert(r, i) {
    const _id = r.hexId();
    const person = fakePerson(`expert-${i}`);

    return {
        _id,
        id: _id,
        name: person.full,
        email: person.email,
        color: r.pick(['#F97316', '#2563EB', '#16A34A', '#DB2777', '#9333EA', '#0891B2']),
    };
}

/** Registry: resource → blueprint. */
const BLUEPRINTS = {
    order: bpOrder,
    workOrder: bpWorkOrder,
    booking: bpBooking,
    customer: bpCustomer,
    invoice: bpInvoice,
    navInvoice: bpInvoice,
    lead: bpLead,
    quote: bpQuote,
    technician: bpTechnician,
    appointment: bpAppointment,
    stockItem: bpStockItem,
    transaction: bpTransaction,
    warehouse: bpWarehouse,
    bank: bpBank,
    register: bpRegister,
    costType: bpCostType,
    integration: bpIntegration,
    user: bpUser,
    automation: bpAutomation,
    guest: bpGuest,
    vipCard: bpVipCard,
    calendarEvent: bpCalendarEvent,
    calendarExpert: bpCalendarExpert,
    haccpEquipment: bpHaccpEquipment,
    haccpRecord: bpHaccpRecord,
    voipCall: bpVoipCall,
    voipContact: bpVoipContact,
};

/**
 * Whether a synthetic blueprint exists for a resource.
 * @param {string} resource resource key.
 * @returns {boolean} true when {@link buildEntities} can generate it.
 */
export function hasBlueprint(resource) {
    return typeof BLUEPRINTS[resource] === 'function';
}

/**
 * Build `count` deterministic synthetic entities for a resource.
 * @param {string} resource resource key (e.g. `order`, `lead`).
 * @param {number} count how many rows to fabricate.
 * @param {object} [ctx] enrichment context ({startMs,endMs,nowMs,restaurantId,placeId,direction,seedSalt}).
 * @returns {object[]} the fabricated rows (empty array if no blueprint / on error).
 */
export function buildEntities(resource, count, ctx = {}) {
    const gen = BLUEPRINTS[resource];
    if (typeof gen !== 'function' || !(count > 0)) {
        return [];
    }
    const nowMs = ctx.nowMs || Date.now();
    const safeCtx = {
        nowMs,
        startMs: ctx.startMs || (nowMs - 14 * 86400000),
        endMs: ctx.endMs || nowMs,
        restaurantId: ctx.restaurantId || '',
        placeId: ctx.placeId || '',
        direction: ctx.direction || '',
        lang: ctx.lang || 'hu',
    };
    const out = [];
    for (let i = 0; i < count; i++) {
        try {
            // A stable rng per row: `resource|salt|index`. The optional salt (e.g.
            // the date window) lets two different windows fabricate different but
            // individually-stable data.
            out.push(gen(createRng(`${resource}|${ctx.seedSalt || ''}|${i}`), i, safeCtx));
        } catch (_) {
            // one bad row must never break the batch
        }
    }

    return out;
}
